import { laravelGet } from './laravelClient';
import { ApiError } from './laravelError';
import { loyaltySession } from './session';

const LOGOUT_SKIP_AGGRESSIVE_KEY = 'loyalty_logout_skip_aggressive_restore';
const LOGOUT_SKIP_AGGRESSIVE_MS = 90_000;

/** Cookie-only legacy sessions — retries after the page is fully loaded. */
const POST_LOAD_DELAYS_MS = [0, 250, 600, 1200, 2000];

/** QR / camera cold opens — cookies and storage can lag behind a normal tab open. */
const QR_EXTERNAL_DELAYS_MS = [0, 400, 900, 1600, 2500, 4000, 5500];

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function markLoyaltyLoggedOut() {
  try {
    sessionStorage.setItem(LOGOUT_SKIP_AGGRESSIVE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearLoyaltyLogoutMarker() {
  try {
    sessionStorage.removeItem(LOGOUT_SKIP_AGGRESSIVE_KEY);
  } catch {
    // ignore
  }
}

/** True for QR / cold opens; false right after the user clicked logout. */
export function shouldUseAggressiveRestore() {
  try {
    const raw = sessionStorage.getItem(LOGOUT_SKIP_AGGRESSIVE_KEY);
    if (!raw) {
      return true;
    }
    const loggedOutAt = Number(raw);
    if (!Number.isFinite(loggedOutAt)) {
      sessionStorage.removeItem(LOGOUT_SKIP_AGGRESSIVE_KEY);
      return true;
    }
    if (Date.now() - loggedOutAt > LOGOUT_SKIP_AGGRESSIVE_MS) {
      sessionStorage.removeItem(LOGOUT_SKIP_AGGRESSIVE_KEY);
      return true;
    }
    return false;
  } catch {
    return true;
  }
}

async function fetchLoyaltySessionUser() {
  const data = await laravelGet('/loyalty/session', {
    auth: 'loyalty',
    context: { redirectOnFailure: false },
  });

  return data?.user ?? null;
}

async function validateSessionOnce({ clearOn401 = true } = {}) {
  try {
    const user = await fetchLoyaltySessionUser();
    if (user) {
      loyaltySession.setSession(user);
      clearLoyaltyLogoutMarker();
      return user;
    }
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      if (clearOn401) {
        loyaltySession.clear();
      }
      return null;
    }
    throw error;
  }
  return null;
}

async function validateWithDelays(delays) {
  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    const delay = delays[attempt] ?? 0;
    if (delay > 0) {
      await sleep(delay);
    }

    try {
      const user = await fetchLoyaltySessionUser();
      if (user) {
        loyaltySession.setSession(user);
        clearLoyaltyLogoutMarker();
        return user;
      }
    } catch (error) {
      const isUnauthorized = error instanceof ApiError && error.status === 401;

      if (isUnauthorized) {
        if (attempt >= delays.length - 1) {
          loyaltySession.clear();
          return null;
        }
        continue;
      }

      if (attempt >= delays.length - 1) {
        break;
      }
    }
  }

  return null;
}

/** True when localStorage or memory holds something worth validating with the API. */
export function hasStoredLoyaltyAuthHints() {
  return Boolean(loyaltySession.getBearerToken() || loyaltySession.getUser());
}

/** Resolves when the document and subresources have finished loading. */
export function waitForPageFullyLoaded() {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    if (document.readyState === 'complete') {
      resolve();
      return;
    }
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

/**
 * After the login page is fully loaded, validate stored bearer / cookie and sign in.
 * Used on the login form so mobile Safari has time to attach cross-subdomain cookies.
 */
export async function attemptLoyaltyAutoLogin({ fromQrOrExternal = false } = {}) {
  if (!shouldUseAggressiveRestore() && !hasStoredLoyaltyAuthHints()) {
    return null;
  }

  await waitForPageFullyLoaded();

  const hasBearer = Boolean(loyaltySession.getBearerToken());
  const delays = fromQrOrExternal ? QR_EXTERNAL_DELAYS_MS : POST_LOAD_DELAYS_MS;

  if (hasBearer) {
    const user = await validateSessionOnce({ clearOn401: false });
    if (user) {
      return user;
    }
    return validateWithDelays([0, 300, 800]);
  }

  if (!shouldUseAggressiveRestore()) {
    return validateSessionOnce({ clearOn401: true });
  }

  return validateWithDelays(delays);
}

/**
 * Restore loyalty client state from persisted bearer token or httpOnly cookie.
 * Bearer in localStorage fixes mobile Safari QR cold opens where cross-subdomain
 * cookies are not sent on the first request.
 */
export async function restoreLoyaltySession({
  forceValidate = false,
  waitForPageShow: waitForPageShowEnabled = false,
} = {}) {
  const cached = loyaltySession.getUser();
  const hasBearer = Boolean(loyaltySession.getBearerToken());

  if (cached && !forceValidate && loyaltySession.isAuthenticated()) {
    return cached;
  }

  const afterLogout = !shouldUseAggressiveRestore();

  if (hasBearer) {
    return validateSessionOnce();
  }

  if (afterLogout) {
    return validateSessionOnce();
  }

  const delays = waitForPageShowEnabled ? POST_LOAD_DELAYS_MS : [0];
  return validateWithDelays(delays);
}

/**
 * Where to send a member after a successful session restore or login.
 */
export function resolveLoyaltyLandingPath(search = '') {
  const params = new URLSearchParams(search);
  const next = params.get('next') || params.get('redirect');
  if (next && next.startsWith('/loyalty/') && !next.startsWith('//')) {
    return next;
  }
  return '/loyalty/profile';
}

/**
 * Full-page navigation after auth — avoids client-router races on cold QR opens.
 */
export function redirectToLoyaltyLanding(search = '') {
  if (typeof window === 'undefined') {
    return;
  }
  clearLoyaltyLogoutMarker();
  window.location.replace(resolveLoyaltyLandingPath(search));
}
